import React, {useState, useEffect} from "react";
import styled from "styled-components";
import { useState } from "react";
import { getUserId } from "../../../firebase";
import { SpinnerDotted } from "spinners-react";
import CountBadge from "./CountBadge";
import ProgressBadge from "./ProgressBadge";
import BadgeDetails from "./BadgeDetails";

const BadgesOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    z-index: 150;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: rgba(0,0,0,0.5);
    padding: 20px;
    color: white;
    font-size: 1.5rem;
    font-weight: bold;
    border-radius: 10px;
    margin: 20px;
    max-height: 100vh;
    max-width: 100vw;
    overflow: scroll;
    width: auto;
`

const BadgeContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: left;
    justify-content: center;
    background-color: white;
    border-radius: 10px;
    padding: 10px;
    margin: 10px;
    color: black;
    width: 100%;
`

const BadgeTitle = styled.div`
    font-size: 1.5rem;
    font-weight: bold;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
    width: 100%;
`

const Badge = styled.div`
    display: flex;
    font-size: 1rem;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    background-color: #009E6355;
    border-radius: 10px;
    padding: 10px;
    margin: 3px;
    color: black;
`

const ClosedButton = styled.button`
    background-color: white;
    border-radius: 10px;
    padding: 8px;
    margin: 2px;
    color: black;
    font-size: 1.5rem;
    font-weight: bold;
    background-color: #009E63;
    color: white;
`

const Badges = ({ onClose }) => {
    const [badges, setBadges] = useState({});
    const [loading, setLoading] = useState(true);
    const [usedBadgeColors, setUsedBadgeColors] = useState([]);
    const [selectedBadge, setSelectedBadge] = useState(null);

    useEffect(() => {
        fetch(`https://atlas-api.services.divvit.co/users/${getUserId()}/badges`)
            .then(res => res.json())
            .then(res => setBadges(res))
            .then(() => setLoading(false))
    }, [])

    useEffect(() => {
        if (badges) {
            const colors = Object.values(badges).reduce((acc, badge) => {
                if (badge.currentLevel && !acc.includes(badge.currentLevel.name.toLowerCase())) {
                    return [...acc, badge.currentLevel.name.toLowerCase()];
                } else if (badge.completedLevels && Object.keys(badge.completedLevels).length) {
                    const usedBadges = Object.keys(badge.completedLevels)
                        .filter(level => badge.completedLevels[level] > 0)
                        .filter(level => !acc.includes(level.toLowerCase()));
                    return [...acc, ...usedBadges];
                }
                return acc;
            }, []);
            colors.reverse();
            setUsedBadgeColors(colors);
        }
    }, [badges])

    const getBadgesForColor = (color) => {
        const regular = Object.values(badges).filter(badge => badge.currentLevel && badge.currentLevel.name.toLowerCase() === color);
        const complex = Object.values(badges).filter(badge => badge.completedLevels && Object.keys(badge.completedLevels).length && badge.completedLevels[color] > 0).map(badge => {
            return {
                ...badge,
                badgeCount: badge.completedLevels[color]
            }
        })
        return [...regular, ...complex];
    }

    return <BadgesOverlay>
        <div style={{ display: 'flex' }}>
            {selectedBadge && <ClosedButton onClick={() => setSelectedBadge(null)}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-arrow-left"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                    Back
                </div>
            </ClosedButton>}
            <ClosedButton onClick={onClose}>Close</ClosedButton>
        </div>
        {loading && <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 12 }}>
            <div style={{ height: 60, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <SpinnerDotted size={60} />
            </div>
            <div>Loading badges...</div>
        </div>}
        {!loading && !Object.keys(badges).length && <div>No badges yet!</div>}
        {!loading && !selectedBadge && Object.keys(badges).length && usedBadgeColors.length && usedBadgeColors.map(color => <BadgeContainer key={color}>
            <BadgeTitle>
                <Laurel color={color} />
                <div>{color.substring(0, 1).toUpperCase().concat(color.substring(1))}</div>
            </BadgeTitle>
            {getBadgesForColor(color).map(badge => <div key={badge.name}>
                { badge.badgeCount ? (
                    <CountBadge count={badge.badgeCount} label={badge.name} onClick={() => setSelectedBadge(badge)} />
                ) : (
                    <ProgressBadge onClick={() => setSelectedBadge(badge)} label={badge.name} levels={badge.levels} count={badge.count} />
                )}
            </div>)}
        </BadgeContainer>)}
        {selectedBadge && <BadgeDetails badge={selectedBadge} onClose={() => setSelectedBadge(null)} />}
    </BadgesOverlay>;
}

export default Badges;

const Laurel = ({ color }) => {
    return (
        <svg style={{height: '2.25rem', width: '2.25rem'}} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M0 0h512v512H0z" fill="#000000" fillOpacity="0"></path><g style={{touchAction: 'none'}} transform="translate(0,0)"><path d="M234.406 18.875c-20.994.204-38.772 2.513-61.97 10.156-4.12 1.99-8.196 4.088-12.217 6.282.082.7.2 1.448.374 2.344.675 3.48 2.508 7.86 5.094 12.282-4.92 4.2-9.624 8.466-14.125 12.812-3.64-5.5-6.54-11.35-8.282-17.375-14.227 9.153-27.643 19.636-40.03 31.438.254 2.066 1.026 4.412 2.344 7.25 2.462 5.3 7.06 11.322 12.812 16.812-3.75 5.197-7.253 10.436-10.53 15.75-8.153-7.21-14.987-15.575-19.22-24.688-.006-.012-.025-.018-.03-.03C74.93 107.198 62.838 124.26 52.78 143c1.038 3.078 2.934 6.267 5.69 9.72 5.394 6.76 14.15 13.456 24.124 18.686-1.957 6.096-3.668 12.202-5.125 18.344-13.226-6.337-25.218-14.88-33.595-25.375-.297-.373-.556-.776-.844-1.156-8.69 20.085-14.71 40.67-18.218 61.374 2.068 4.086 5.57 7.838 10.47 11.53 8.717 6.572 21.692 11.736 35.5 14.376.08 6.414.415 12.8.968 19.156-17.653-2.733-34.73-8.835-47.72-18.625-.74-.558-1.47-1.156-2.186-1.75-1.65 22.29-.487 44.524 3.343 66.19 3.41 3.562 8.08 6.44 14.032 8.842 11.875 4.797 28.28 6.51 44.624 4.938 1.954 6.09 4.12 12.106 6.47 18.03-.554.073-1.104.155-1.658.22-19.7 2.324-39.804.843-56.437-5.875-.41-.166-.814-.358-1.22-.53 6.614 23.667 16.443 46.355 29.22 67.405 4.236 2.202 9.33 3.61 15.31 4.344 13.334 1.636 30.26-1.005 46.345-6.875 3.713 5.318 7.583 10.51 11.625 15.53-.364.148-.728.292-1.094.438-18.537 7.39-38.396 11.308-56.437 9.687 12.35 16.255 26.648 31.175 42.718 44.406 4.92 1.056 10.465 1.085 16.687.22 11.763-1.636 25.218-6.96 37.938-14.688 16.692 13.528 34.88 24.752 54.125 33.094l7.437-17.156c-16.007-6.94-31.317-16.184-45.594-27.28 13.276-10.896 24.32-24.018 30.25-36.5 4.633-9.75 6.31-18.457 4.47-26.283-10.724-5.69-20.58-12.555-29.53-20.375-2.564 7.583-6.86 14.73-12.25 21.063-8.268 9.712-19.11 18.113-31.22 24.938-4.03-4.837-7.918-9.846-11.625-15C150.93 391.82 161.08 384.128 168 376c7.28-8.553 10.957-16.73 10.5-25-8.91-10.316-16.57-21.762-22.906-34.094-3.718 4.5-8.17 8.512-13.094 11.906-9.64 6.647-21.16 11.548-33.438 14.813-2.4-5.792-4.603-11.66-6.593-17.625 11.284-2.868 21.645-7.222 29.436-12.594 7.534-5.193 12.477-10.764 14.656-17.062-4.27-11.528-7.497-23.62-9.625-36.094-3.793 2.408-7.92 4.392-12.218 5.906-10.424 3.672-22.125 5.214-34.095 5.063-.638-6.208-1.028-12.46-1.188-18.72 10.71.24 20.952-1.11 29.063-3.97 7.268-2.56 12.544-5.873 15.844-10.28-.823-12.615-.558-25.505.844-38.5-2.74.603-5.538.982-8.344 1.156-10.34.64-21.314-1.062-32.063-4.375 1.35-6.054 2.964-12.082 4.845-18.093 9.264 2.875 18.492 4.282 26.063 3.813 5.284-.327 9.454-1.313 12.687-3.063 2.727-13.632 6.68-27.292 12-40.78-.93-.14-1.867-.28-2.78-.47-8.474-1.77-16.932-5.42-24.845-10.28 3.223-5.27 6.703-10.468 10.438-15.626 6.274 3.737 12.7 6.474 18.218 7.626 2.68.56 4.973.8 6.97.75 5.722-11.6 12.29-22.526 19.56-32.687-5.17-2.72-10-6.365-14.405-10.595 4.415-4.28 9.052-8.508 13.907-12.656 3.257 2.983 6.617 5.433 9.657 6.968.96.484 1.814.842 2.625 1.156 4.562-5.397 9.33-10.563 14.25-15.438 11.515-11.683 23.998-23.865 34.686-38.28-1.43-.005-2.85-.015-4.25 0zm38.375 0c10.69 14.416 23.204 26.598 34.72 38.28 4.914 4.87 9.66 10.047 14.22 15.44.81-.315 1.663-.673 2.624-1.157 3.037-1.534 6.393-3.99 9.656-6.97 4.856 4.15 9.492 8.378 13.906 12.657-4.4 4.238-9.225 7.908-14.406 10.625 7.27 10.16 13.873 21.06 19.594 32.656 1.984.046 4.272-.193 6.937-.75 5.52-1.152 11.945-3.89 18.22-7.625 3.735 5.16 7.215 10.358 10.438 15.626-7.904 4.86-16.34 8.512-24.813 10.28-.924.195-1.873.33-2.813.47 5.325 13.5 9.336 27.168 12.063 40.813 3.224 1.727 7.374 2.705 12.625 3.03 7.563.47 16.8-.944 26.063-3.813 1.88 6.01 3.494 12.04 4.843 18.094-10.75 3.314-21.722 5.017-32.062 4.376-2.807-.174-5.603-.553-8.344-1.156 1.402 12.995 1.666 25.885.844 38.5 3.3 4.407 8.576 7.72 15.844 10.28 8.103 2.856 18.353 4.205 29.062 3.97-.16 6.26-.55 12.512-1.188 18.72-11.97.15-23.67-1.392-34.093-5.064-4.29-1.51-8.402-3.505-12.19-5.906-2.128 12.484-5.347 24.59-9.624 36.125 2.195 6.285 7.136 11.847 14.656 17.03 7.785 5.368 18.133 9.727 29.407 12.595-1.99 5.962-4.197 11.835-6.595 17.625-12.277-3.265-23.804-8.17-33.438-14.813-4.924-3.394-9.375-7.406-13.093-11.906-6.342 12.343-13.987 23.802-22.906 34.125-.447 8.26 3.23 16.428 10.5 24.97 6.914 8.123 17.072 15.822 28.625 22.063-3.706 5.154-7.564 10.163-11.594 15-12.122-6.827-22.98-15.22-31.25-24.938-5.393-6.334-9.69-13.48-12.25-21.063-8.956 7.824-18.802 14.683-29.533 20.375-1.83 7.826-.13 16.532 4.5 26.282 5.93 12.482 16.976 25.604 30.25 36.5-14.274 11.096-29.586 20.34-45.593 27.28L284 494.656c19.25-8.345 37.43-19.592 54.125-33.125 12.718 7.736 26.167 13.083 37.938 14.72 6.247.87 11.816.85 16.75-.22 16.062-13.227 30.34-28.156 42.687-44.405-18.05 1.63-37.92-2.29-56.47-9.688-.364-.145-.728-.29-1.092-.437 4.042-5.02 7.912-10.212 11.625-15.53 16.09 5.876 33.003 8.51 46.343 6.874 5.98-.734 11.075-2.142 15.313-4.344 12.77-21.042 22.603-43.717 29.217-67.375-.393.168-.79.34-1.187.5-16.633 6.718-36.738 8.2-56.438 5.875-.563-.066-1.124-.146-1.687-.22 2.35-5.924 4.515-11.94 6.47-18.03 16.354 1.577 32.77-.138 44.655-4.938 5.937-2.397 10.6-5.26 14-8.812 3.836-21.685 5.028-43.94 3.375-66.25-.726.602-1.465 1.213-2.22 1.78-12.987 9.79-30.065 15.893-47.717 18.626.552-6.355.888-12.742.968-19.156 13.813-2.638 26.776-7.8 35.5-14.375 4.9-3.693 8.402-7.445 10.47-11.53-3.507-20.706-9.53-41.29-18.22-61.376-.288.38-.546.782-.844 1.155-8.376 10.496-20.368 19.038-33.593 25.375-1.456-6.132-3.173-12.226-5.126-18.313 9.98-5.233 18.723-11.95 24.125-18.718 2.754-3.453 4.65-6.642 5.686-9.72-10.056-18.736-22.152-35.772-35.844-51.063-4.232 9.113-11.09 17.477-19.25 24.688-3.278-5.314-6.78-10.553-10.53-15.75 5.752-5.49 10.35-11.51 12.812-16.813 1.318-2.838 2.115-5.183 2.375-7.25-12.388-11.8-25.836-22.284-40.064-31.437-1.743 6.025-4.64 11.874-8.28 17.375-4.493-4.336-9.188-8.59-14.095-12.78 2.584-4.428 4.387-8.827 5.064-12.314.174-.896.29-1.644.375-2.343-4.02-2.193-8.07-4.295-12.19-6.282-24.742-8.152-43.36-10.22-66.25-10.155z" fill={color} fillOpacity="1"></path></g></svg>
    )
}